import * as React from "react";
import { deleteOrNop, setSafe } from "../util/storeHelper";
import * as PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import { clearImmediate, setImmediate } from "timers";
import { truthy } from "../util/util";
export { connect } from "react-redux";
export { extend } from "../ExtensionProvider";
const translate = withTranslation;
// react-i18next typings are borked atm, forcing the props of the wrapped class
// to declare all parameter it injects as non-optional, meaning you're not allowed
// to have a class that can also work without (some of) them.
export { translate };
export class StateProxyHandler {
    mComponent;
    mPath;
    mBaseObject;
    mParent;
    mSubProxies;
    mDelayed;
    mDelayedTimer;
    constructor(component, baseObject, parent, objPath, delayed) {
        this.mComponent = component;
        this.mPath = objPath;
        this.mBaseObject = baseObject;
        this.mParent = parent;
        this.mSubProxies = {};
        this.mDelayed = delayed;
    }
    has(target, key) {
        return key in target;
    }
    get(target, key) {
        return this.derive(target, key);
    }
    deleteProperty(target, key) {
        delete target[key];
        const fullPath = [].concat(this.mPath, key);
        this.setBaseObject(deleteOrNop(this.baseObject(), fullPath));
        this.mComponent.setState(this.baseObject());
        return true;
    }
    set(target, key, value, receiver) {
        target[key] = value;
        const fullPath = [].concat(this.mPath, key);
        this.setBaseObject(setSafe(this.baseObject(), fullPath, value));
        return true;
    }
    baseObject() {
        if (this.mParent === undefined) {
            return this.mBaseObject;
        }
        else {
            return this.mParent.baseObject();
        }
    }
    setBaseObject(newObj) {
        if (this.mParent === undefined) {
            this.mBaseObject = newObj;
            if (this.mDelayed) {
                if (this.mDelayedTimer !== undefined) {
                    clearImmediate(this.mDelayedTimer);
                }
                this.mDelayedTimer = setImmediate(() => {
                    this.mComponent.setState(this.mBaseObject);
                    this.mDelayedTimer = undefined;
                });
            }
            else {
                this.mComponent.setState(this.mBaseObject);
            }
        }
        else {
            this.mParent.setBaseObject(newObj);
        }
    }
    derive(obj, key) {
        if (typeof obj[key] !== "object" ||
            typeof key !== "string" ||
            !truthy(obj[key])) {
            return obj[key];
        }
        if (!(key in this.mSubProxies) || obj[key] !== this.mSubProxies[key].obj) {
            this.mSubProxies[key] = {
                proxy: new Proxy(obj[key], new StateProxyHandler(this.mComponent, null, this, [].concat(this.mPath, key), this.mDelayed)),
                obj: obj[key],
            };
        }
        return this.mSubProxies[key].proxy;
    }
}
/**
 * convenience extension for React.Component that adds support for the
 * i18n library.
 *
 * This whole module is just here to reduce the code required for "decorated"
 * components.
 *
 * @export
 * @class ComponentEx
 * @extends {(React.Component<P & II18NProps, S>)}
 * @template P
 * @template S
 */
export class ComponentEx extends React.Component {
    static contextTypes = {
        api: PropTypes.object.isRequired,
        menuLayer: PropTypes.object,
        getModifiers: PropTypes.func,
    };
    nextState;
    initState(value, delayed = false) {
        this.state = JSON.parse(JSON.stringify(value));
        const proxyHandler = new StateProxyHandler(this, value, undefined, [], delayed);
        this.nextState = new Proxy(value, proxyHandler);
    }
}
export class PureComponentEx extends React.PureComponent {
    static contextTypes = {
        api: PropTypes.object.isRequired,
        menuLayer: PropTypes.object,
        getModifiers: PropTypes.func,
    };
    nextState;
    initState(value, delayed = false) {
        this.state = JSON.parse(JSON.stringify(value));
        const proxyHandler = new StateProxyHandler(this, value, undefined, [], delayed);
        this.nextState = new Proxy(value, proxyHandler);
    }
}
